import type {
  RestoreAccount,
  GcpDiskSnapshotProperties,
  InventoryResource,
  Snapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { FEATURES_SUPPORT_VERSIONS } from '@/configs/featureConfig';

import { CrossCloudInstanceAdapter } from './crossCloudInstanceAdapter';
import { CrossCloudVolumeAdapter } from './crossCloudVolumeAdapter';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { GcpNetwork } from '../selectors/gcpNetworkSelect';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface GcpDiskConfigState {
  diskSettings: GcpDiskSnapshotProperties;
  keepOriginalTags: boolean;
  keepOriginalSettings: boolean;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  encryptionKey?: string;
}

export interface RestoreGcpVMState {
  resourceId: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  supportedInstalledVersion?: string;
  restoreAccount?: RestoreAccount;
  vmName: string;
  machineType: string;
  network?: GcpNetwork;
  subnetName?: string;
  regionName?: string;
  keepOriginalTags: boolean;
  startInstanceAfterRestore: boolean;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  disksState: GcpDiskConfigState[];
  initialDiskStates: GcpDiskConfigState[];
  zone?: string;
  sharedEncryption: {
    isEncrypted: boolean;
    encryptionKeyId: string | undefined;
  };
}

const restoreGcpVMInstanceFlow: Step<RestoreGcpVMState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'gcp_vm_configuration',
      Component: CrossCloudInstanceAdapter,
      next: [
        {
          id: 'disk_configuration',
          Component: CrossCloudVolumeAdapter,
        },
      ],
    },
  ],
};

export const RestoreGcpVMWizard = ({
  snapshot,
  onClose,
  onBack,
}: {
  resource?: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const diskStates =
    snapshot.resource?.properties?.gcpVm?.disks?.map((disk) => ({
      diskSettings: {
        ...disk,
        diskName: `${disk.diskName ?? ''}-restored`,
      },
      diskId: disk.diskId ?? '',
      diskName: `${disk.diskName ?? ''}-restored`,
      diskType: disk.diskType ?? '',
      diskSizeBytes: disk.diskSizeBytes ?? 0,
      keepOriginalTags: true,
      keepOriginalSettings: true,
      initialTags: disk.tags || {},
      tags: disk.tags || {},
    })) ?? [];

  const machineType =
    snapshot.resource?.properties?.gcpVm?.machineType?.split('/')?.pop() || '';

  return (
    <Wizard<RestoreGcpVMState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreGcpVMInstanceFlow]}
      initialState={{
        snapshot,
        resourceId: snapshot.resourceId,
        startInstanceAfterRestore: true,
        supportedCloudProviders: [Provider.Gcp],
        supportedInstalledVersion:
          FEATURES_SUPPORT_VERSIONS.GCP_RESTORE_ACCOUNT_RESTORE_ACCOUNT,
        keepOriginalTags: true,
        vmName: `${snapshot.resource?.properties?.gcpVm?.name ?? ''}-restored`,
        machineType,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        initialDiskStates: diskStates,
        disksState: diskStates,
        regionName: snapshot.resource?.properties?.gcpVm?.region || 'us-east1',
        sharedEncryption: {
          isEncrypted: true,
          encryptionKeyId: undefined,
        },
      }}
      title={`Restore GCE instance`}
      stepperLabels={[
        'Restore account',
        'Instance configuration',
        'Disk configuration',
      ]}
    />
  );
};
